import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { isCircuitOpen, trackQueryResult, onCircuitChange } from "@/lib/circuit-breaker";
import { STRIPE_PLANS } from "@/lib/stripe-plans";

interface SubscriptionContextType {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
  checkSubscription: () => Promise<void>;
  isAdmin: boolean;
  shouldShowPaywall: boolean;
  status: string | null;
  inGracePeriod: boolean;
  gracePeriodEnd: string | null;
  failureCount: number;
  lastPaymentError: string | null;
  isWorkspaceMember: boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const SESSION_CACHE_KEY = "fabchat_subscription_cache";
const INTERNAL_WS_CACHE_PREFIX = "fabchat_internal_ws_";
const INTERNAL_WS_CACHE_TTL_MS = 10 * 60 * 1000;

type InternalWorkspaceCache = { isInternal: boolean; timestamp: number };

function loadInternalWorkspaceCache(workspaceId: string): InternalWorkspaceCache | null {
  try {
    const raw = sessionStorage.getItem(`${INTERNAL_WS_CACHE_PREFIX}${workspaceId}`);
    if (!raw) return null;
    const parsed: InternalWorkspaceCache = JSON.parse(raw);
    if (Date.now() - parsed.timestamp < INTERNAL_WS_CACHE_TTL_MS) return parsed;
    return null;
  } catch {
    return null;
  }
}

function saveInternalWorkspaceCache(workspaceId: string, isInternal: boolean) {
  try {
    sessionStorage.setItem(
      `${INTERNAL_WS_CACHE_PREFIX}${workspaceId}`,
      JSON.stringify({ isInternal, timestamp: Date.now() } satisfies InternalWorkspaceCache),
    );
  } catch {
    // ignore
  }
}

interface CachedSubscription {
  subscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  status: string | null;
  inGracePeriod: boolean;
  gracePeriodEnd: string | null;
  failureCount: number;
  lastPaymentError: string | null;
  isWorkspaceMember: boolean;
  isAdmin: boolean;
  userId: string;
  timestamp: number;
}

/** Status em que a cobrança ainda pode compensar (boleto) — mantém acesso na tolerância. */
export const GRACE_ELIGIBLE_STATUSES = ["past_due", "unpaid", "incomplete"];

function hasValidGracePeriod(data: {
  status?: string | null;
  grace_period_end?: string | null;
  gracePeriodEnd?: string | null;
}): boolean {
  const graceEnd = data.grace_period_end ?? data.gracePeriodEnd;
  if (!graceEnd || !GRACE_ELIGIBLE_STATUSES.includes(data.status ?? "")) return false;
  const graceEndMs = new Date(graceEnd).getTime();
  return Number.isFinite(graceEndMs) && graceEndMs > Date.now();
}

function saveToSessionCache(data: CachedSubscription) {
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(data));
  } catch { /* ignore */ }
}

function loadFromSessionCache(userId: string): CachedSubscription | null {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed: CachedSubscription = JSON.parse(raw);
    if (parsed.userId === userId && Date.now() - parsed.timestamp < 10 * 60 * 1000) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function isTransientError(error: any): boolean {
  const msg = error?.message || error?.details || "";
  const code = error?.code || "";
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("NetworkError") ||
    msg.includes("timeout") ||
    msg.includes("schema cache") ||
    msg.includes("PGRST002") ||
    msg.includes("connection") ||
    msg.includes("503") || msg.includes("504") || msg.includes("502") ||
    msg.includes("57014") ||
    msg.includes("upstream request timeout") ||
    code === "PGRST002" || code === "57014" ||
    // Token expirado/inválido: é temporário (renovação), nunca "sem assinatura"
    msg.includes("JWT expired") || msg.includes("JWT") ||
    code === "PGRST301" || code === "PGRST303"
  );
}

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [gracePeriodEnd, setGracePeriodEnd] = useState<string | null>(null);
  const [failureCount, setFailureCount] = useState(0);
  const [lastPaymentError, setLastPaymentError] = useState<string | null>(null);
  const [isWorkspaceMember, setIsWorkspaceMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [hasTransientError, setHasTransientError] = useState(false);
  const hasTransientErrorRef = useRef(false);
  const { user, session } = useAuth();

  // OPTIMIZATION: Reuse workspace_id and member role from singleton store to avoid duplicate queries
  const { workspaceId: storeWorkspaceId, memberRole: storeMemberRole } = useWorkspace(user?.id);

  const hasCheckedRef = useRef(false);
  const isCheckingRef = useRef(false);
  const cachedSubscribedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);
  const pendingRecheckRef = useRef(false);
  const lastCheckedWorkspaceIdRef = useRef<string | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const realtimeChannelRef = useRef<any>(null);
  const wsRealtimeChannelRef = useRef<any>(null);
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const REALTIME_DEBOUNCE_MS = 2500;
  const subscriptionCheckArgsRef = useRef({
    userId: user?.id ?? null,
    workspaceId: storeWorkspaceId,
    role: storeMemberRole,
  });
  subscriptionCheckArgsRef.current = {
    userId: user?.id ?? null,
    workspaceId: storeWorkspaceId,
    role: storeMemberRole,
  };

  const scheduleRealtimeSubscriptionCheck = () => {
    if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    realtimeDebounceRef.current = setTimeout(() => {
      realtimeDebounceRef.current = null;
      const { userId: uid, workspaceId: wid, role: r } = subscriptionCheckArgsRef.current;
      if (!uid) return;
      checkSubscriptionFromDB(uid, wid, 1, r).then((found) => {
        if (!found && !hasTransientErrorRef.current) resetSubscriptionData();
      });
    }, REALTIME_DEBOUNCE_MS);
  };

  // isAdmin is now set ONLY when workspace is internal (master admin accounts)
  // via the is_internal_workspace RPC check in checkSubscriptionFromDB

  const applySubscriptionData = (data: any, userId?: string) => {
    // A tolerância válida autoriza o acesso mesmo se uma resposta/cache antigo
    // ainda trouxer subscribed=false.
    const validGrace = hasValidGracePeriod(data ?? {});
    const sub = Boolean(data?.subscribed || validGrace);
    setSubscribed(sub);
    cachedSubscribedRef.current = sub;
    setProductId(data?.product_id || null);
    setSubscriptionEnd(data?.subscription_end || null);
    setStatus(data?.status || null);
    setInGracePeriod(validGrace);
    setGracePeriodEnd(data?.grace_period_end || null);
    setFailureCount(data?.failure_count || 0);
    setLastPaymentError(data?.last_payment_error || null);
    setIsWorkspaceMember(data?.is_workspace_member || false);

    if (userId) {
      saveToSessionCache({
        subscribed: sub,
        productId: data?.product_id || null,
        subscriptionEnd: data?.subscription_end || null,
        status: data?.status || null,
        inGracePeriod: validGrace,
        gracePeriodEnd: data?.grace_period_end || null,
        failureCount: data?.failure_count || 0,
        lastPaymentError: data?.last_payment_error || null,
        isWorkspaceMember: data?.is_workspace_member || false,
        isAdmin: false,
        userId,
        timestamp: Date.now(),
      });
    }
  };

  const resetSubscriptionData = () => {
    setSubscribed(false);
    cachedSubscribedRef.current = false;
    setProductId(null);
    setSubscriptionEnd(null);
    setStatus(null);
    setInGracePeriod(false);
    setGracePeriodEnd(null);
    setFailureCount(0);
    setLastPaymentError(null);
    setIsWorkspaceMember(false);
  };

  const MAX_DB_RETRIES = 3;

  const checkSubscriptionFromDB = async (
    userId: string,
    workspaceId: string | null,
    attempt = 1,
    roleFromStore?: string | null
  ): Promise<boolean> => {
    if (isCircuitOpen()) {
      console.log("[Subscription] Circuit breaker OPEN, skipping query (degraded mode)");
      const cached = loadFromSessionCache(userId);
      if (cached) {
        const cachedHasAccess = cached.subscribed || hasValidGracePeriod(cached);
        setSubscribed(cachedHasAccess);
        cachedSubscribedRef.current = cachedHasAccess;
        setIsAdmin(cached.isAdmin);
        setLoading(false);
        return cachedHasAccess;
      }
      // Sem cache: destrava UI em modo degradado e agenda retry em background.
      setHasTransientError(true);
      hasTransientErrorRef.current = true;
      setLoading(false);
      startBackgroundRetry(userId);
      return cachedSubscribedRef.current;
    }

    try {
      // Sessão expirada (ex.: aba/PWA voltando do background) faz o banco responder
      // "JWT expired" e a checagem concluía "sem assinatura" → paywall falso.
      // Renova o token antes de consultar.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const expMs = (session?.expires_at ?? 0) * 1000;
        if (session && expMs - Date.now() < 60_000) {
          await supabase.auth.refreshSession();
        }
      } catch { /* segue; erro de auth abaixo vira retry */ }

      let memberRole: string | null = roleFromStore ?? null;
      let resolvedWorkspaceId = workspaceId;

      // When we have role from workspace store, skip workspace_members queries
      if (resolvedWorkspaceId && memberRole != null) {
        // use store role as-is
      } else if (resolvedWorkspaceId) {
        const { data: memberData, error: memberError } = await supabase
          .from("workspace_members")
          .select("role")
          .eq("user_id", userId)
          .eq("workspace_id", resolvedWorkspaceId)
          .maybeSingle();

        trackQueryResult(memberError);

        if (memberError && isTransientError(memberError) && attempt < MAX_DB_RETRIES) {
          const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
          await new Promise(r => setTimeout(r, delay));
          return checkSubscriptionFromDB(userId, workspaceId, attempt + 1, roleFromStore);
        }

        memberRole = memberData?.role || null;
      } else {
        const { data: memberData, error: memberError } = await supabase
          .from("workspace_members")
          .select("workspace_id, role")
          .eq("user_id", userId)
          .maybeSingle();

        trackQueryResult(memberError);

        if (memberError && isTransientError(memberError) && attempt < MAX_DB_RETRIES) {
          const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
          await new Promise(r => setTimeout(r, delay));
          return checkSubscriptionFromDB(userId, workspaceId, attempt + 1, roleFromStore);
        }

        memberRole = memberData?.role || null;
        resolvedWorkspaceId = memberData?.workspace_id || null;
      }

      // Master/internal workspace FIRST: never depends on Stripe state
      // (old expired/incomplete subscriptions must not trigger the paywall).
      if (resolvedWorkspaceId) {
        const cachedInternal = loadInternalWorkspaceCache(resolvedWorkspaceId);
        let internal = cachedInternal?.isInternal === true;
        if (!cachedInternal) {
          const { data: isInternalEarly, error: internalEarlyErr } = await supabase
            .rpc("is_internal_workspace" as any, { _workspace_id: resolvedWorkspaceId });
          trackQueryResult(internalEarlyErr);
          if (!internalEarlyErr) {
            internal = isInternalEarly === true;
            saveInternalWorkspaceCache(resolvedWorkspaceId, internal);
          }
        }
        if (internal) {
          applySubscriptionData({
            subscribed: true,
            product_id: STRIPE_PLANS.pro.productId,
            subscription_end: null,
            status: memberRole === "owner" ? "internal_owner" : "internal_member",
            is_workspace_member: memberRole !== "owner",
            in_grace_period: false,
            grace_period_end: null,
            failure_count: 0,
            last_payment_error: null,
          }, userId);
          setIsAdmin(true);
          return true;
        }
      }

      // Check own subscription first
      const { data: ownSub, error: subError } = await supabase
        .from("user_subscriptions")
        .select("*")
        .eq("user_id", userId)
        .in("status", ["active", "past_due", "trialing"])
        .maybeSingle();

      trackQueryResult(subError);

      if (subError && isTransientError(subError) && attempt < MAX_DB_RETRIES) {
        const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
        await new Promise(r => setTimeout(r, delay));
        return checkSubscriptionFromDB(userId, workspaceId, attempt + 1);
      }

      if (!subError && ownSub) {
        const isPastDue = GRACE_ELIGIBLE_STATUSES.includes(ownSub.status);
        const isInGracePeriod = Boolean(
          isPastDue &&
          ownSub.grace_period_end &&
          new Date(ownSub.grace_period_end).getTime() > Date.now(),
        );
        const hasAccess = ownSub.status === "active" || ownSub.status === "trialing" || isInGracePeriod;
        if (!hasAccess) {
          // Continue para a checagem do workspace/provedor; past_due expirado não libera acesso.
        } else {
        applySubscriptionData({
          subscribed: true,
          product_id: ownSub.product_id,
          subscription_end: ownSub.current_period_end,
          status: ownSub.status,
          in_grace_period: isInGracePeriod,
          grace_period_end: isInGracePeriod ? ownSub.grace_period_end : null,
          failure_count: ownSub.failure_count || 0,
          last_payment_error: ownSub.last_payment_error || null,
          is_workspace_member: false,
        }, userId);
        return true;
        }
      }

      // Check workspace subscription (members/admins inherit workspace subscription by workspace_id).
      // Hardening: order+limit avoids maybeSingle() failing with duplicates.
      if (resolvedWorkspaceId) {
        const { data: workspaceSub, error: workspaceSubError } = await supabase
          .from("user_subscriptions")
          .select("*")
          .eq("workspace_id", resolvedWorkspaceId)
          .in("status", ["active", "trialing", ...GRACE_ELIGIBLE_STATUSES])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        trackQueryResult(workspaceSubError);

        if (workspaceSub) {
          const isPastDue = GRACE_ELIGIBLE_STATUSES.includes(workspaceSub.status);
          const isInGracePeriod = Boolean(
            isPastDue &&
            workspaceSub.grace_period_end &&
            new Date(workspaceSub.grace_period_end).getTime() > Date.now(),
          );
          const hasAccess = workspaceSub.status === "active" || workspaceSub.status === "trialing" || isInGracePeriod;
          if (!hasAccess) {
            // Continue para a atualização no provedor; tolerância expirada não libera membros.
          } else {
          applySubscriptionData({
            subscribed: true,
            product_id: workspaceSub.product_id,
            subscription_end: workspaceSub.current_period_end,
            status: memberRole === "owner" ? workspaceSub.status : "member",
            in_grace_period: isInGracePeriod,
            grace_period_end: isInGracePeriod ? workspaceSub.grace_period_end : null,
            failure_count: workspaceSub.failure_count || 0,
            last_payment_error: workspaceSub.last_payment_error || null,
            is_workspace_member: memberRole !== "owner",
          }, userId);
          return true;
          }
        }
      }

      // Fallback auto-curativo: se o banco ficou preso em uma assinatura antiga
      // (ex.: boleto atrasado + nova cobrança/assinatura no Stripe), a função
      // consulta o provedor e atualiza user_subscriptions antes de mostrar paywall.
      if (attempt === 1) {
        const { data: refreshedSub, error: refreshError } = await supabase.functions.invoke("check-subscription");

        trackQueryResult(refreshError);

        if (!refreshError && refreshedSub?.subscribed) {
          applySubscriptionData({
            subscribed: true,
            product_id: refreshedSub.product_id,
            subscription_end: refreshedSub.subscription_end,
            status: refreshedSub.status,
            in_grace_period: refreshedSub.in_grace_period || false,
            grace_period_end: refreshedSub.grace_period_end || null,
            failure_count: refreshedSub.failure_count || 0,
            last_payment_error: refreshedSub.last_payment_error || null,
            is_workspace_member: refreshedSub.is_workspace_member || false,
          }, userId);
          return true;
        }
      }

      // Internal workspace bypass (admin master): allow members of workspaces whose owner has global role 'owner'.
      // This is a secure bypass (membership-checked in SQL) and avoids extra load by caching in sessionStorage.
      if (resolvedWorkspaceId) {
        const cachedInternal = loadInternalWorkspaceCache(resolvedWorkspaceId);
        if (cachedInternal) {
          if (cachedInternal.isInternal) {
            applySubscriptionData({
              subscribed: true,
              product_id: STRIPE_PLANS.pro.productId,
              subscription_end: null,
              status: memberRole === "owner" ? "internal_owner" : "internal_member",
              is_workspace_member: memberRole !== "owner",
              in_grace_period: false,
              grace_period_end: null,
              failure_count: 0,
              last_payment_error: null,
            }, userId);
            setIsAdmin(true);
            return true;
          }
        } else {
          const { data: isInternal, error: internalErr } = await supabase
            .rpc("is_internal_workspace" as any, { _workspace_id: resolvedWorkspaceId });
          trackQueryResult(internalErr);
          if (internalErr && isTransientError(internalErr) && attempt < MAX_DB_RETRIES) {
            const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
            await new Promise(r => setTimeout(r, delay));
            return checkSubscriptionFromDB(userId, workspaceId, attempt + 1, roleFromStore);
          }
          // Se a RPC não existir/ainda não foi aplicada, não cachear falso (permite retry após aplicar SQL)
          if (!internalErr) {
            const internal = isInternal === true;
            saveInternalWorkspaceCache(resolvedWorkspaceId, internal);
            if (internal) {
              applySubscriptionData({
                subscribed: true,
                product_id: STRIPE_PLANS.pro.productId,
                subscription_end: null,
                status: memberRole === "owner" ? "internal_owner" : "internal_member",
                is_workspace_member: memberRole !== "owner",
                in_grace_period: false,
                grace_period_end: null,
                failure_count: 0,
                last_payment_error: null,
              }, userId);
              setIsAdmin(true);
              return true;
            }
          }
        }
      }

      // Not an internal workspace — ensure isAdmin is false
      setIsAdmin(false);
      setHasTransientError(false);
      hasTransientErrorRef.current = false;
      return false;
    } catch (err: any) {
      if (isTransientError(err) && attempt < MAX_DB_RETRIES) {
        const delay = Math.min(2000 * Math.pow(2, attempt - 1), 15000);
        await new Promise(r => setTimeout(r, delay));
        return checkSubscriptionFromDB(userId, workspaceId, attempt + 1);
      }
      if (isTransientError(err)) {
        setHasTransientError(true);
        hasTransientErrorRef.current = true;
        const cached = loadFromSessionCache(userId);
        if (cached) {
          const cachedHasAccess = cached.subscribed || hasValidGracePeriod(cached);
          setSubscribed(cachedHasAccess);
          cachedSubscribedRef.current = cachedHasAccess;
          setIsAdmin(cached.isAdmin);
          return cachedHasAccess;
        }
        startBackgroundRetry(userId);
        return cachedSubscribedRef.current;
      }
      return false;
    }
  };

  const startBackgroundRetry = (userId: string) => {
    if (retryTimerRef.current) return;
    console.log("[Subscription] Starting background retry every 120s");
    retryTimerRef.current = setInterval(async () => {
      console.log("[Subscription] Background retry attempt...");
      const found = await checkSubscriptionFromDB(userId, storeWorkspaceId, 1, storeMemberRole);
      if (!hasTransientError || found) {
        console.log("[Subscription] Background retry succeeded, stopping");
        if (retryTimerRef.current) {
          clearInterval(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        setHasTransientError(false);
        hasTransientErrorRef.current = false;
        setLoading(false);
      }
    }, 120000);
  };

  // Setup Supabase Realtime listeners on user_subscriptions
  const setupRealtimeListeners = (userId: string, workspaceId: string | null) => {
    // Cleanup previous channels
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
      realtimeChannelRef.current = null;
    }
    if (wsRealtimeChannelRef.current) {
      supabase.removeChannel(wsRealtimeChannelRef.current);
      wsRealtimeChannelRef.current = null;
    }

    // Channel 1: Listen by user_id (for owners)
    const userChannel = supabase
      .channel(`subscription-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_subscriptions',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[Subscription] Realtime update (user):', payload.eventType);
          scheduleRealtimeSubscriptionCheck();
        }
      )
      .subscribe();

    realtimeChannelRef.current = userChannel;

    // Channel 2: Listen by workspace_id (for members who inherit subscription)
    if (workspaceId) {
      const wsChannel = supabase
        .channel(`ws-subscription-${workspaceId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_subscriptions',
            filter: `workspace_id=eq.${workspaceId}`,
          },
          (payload) => {
            console.log('[Subscription] Realtime update (workspace):', payload.eventType);
            scheduleRealtimeSubscriptionCheck();
          }
        )
        .subscribe();

      wsRealtimeChannelRef.current = wsChannel;
    }
  };

  const checkSubscription = async () => {
    const userId = user?.id;
    if (!userId || !session) {
      resetSubscriptionData();
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    if (isCheckingRef.current) {
      // If workspace just became available, queue a re-check after current finishes
      if (storeWorkspaceId && lastCheckedWorkspaceIdRef.current !== storeWorkspaceId) {
        pendingRecheckRef.current = true;
      }
      return;
    }
    isCheckingRef.current = true;
    pendingRecheckRef.current = false;

    // Use the latest args from ref to avoid stale closures
    const currentWorkspaceId = subscriptionCheckArgsRef.current.workspaceId;
    const currentRole = subscriptionCheckArgsRef.current.role;
    lastCheckedWorkspaceIdRef.current = currentWorkspaceId;

    try {
      if (!hasCheckedRef.current) {
        setLoading(true);

        // Try sessionStorage cache first for instant UI
        const cached = loadFromSessionCache(userId);
        if (cached) {
          const cachedHasAccess = cached.subscribed || hasValidGracePeriod(cached);
          setSubscribed(cachedHasAccess);
          cachedSubscribedRef.current = cachedHasAccess;
          setProductId(cached.productId);
          setSubscriptionEnd(cached.subscriptionEnd);
          setStatus(cached.status);
          setInGracePeriod(cached.inGracePeriod);
          setGracePeriodEnd(cached.gracePeriodEnd);
          setFailureCount(cached.failureCount);
          setLastPaymentError(cached.lastPaymentError);
          setIsWorkspaceMember(cached.isWorkspaceMember);
          setIsAdmin(cached.isAdmin);
          setLoading(false);
          setInitialCheckDone(true);
        }

        const found = await checkSubscriptionFromDB(userId, currentWorkspaceId, 1, currentRole);
        if (!found && !hasTransientError) {
          resetSubscriptionData();
        }
        setLoading(false);
        hasCheckedRef.current = true;
        setInitialCheckDone(true);
      } else {
        const found = await checkSubscriptionFromDB(userId, currentWorkspaceId, 1, currentRole);
        if (!found && !hasTransientError) {
          resetSubscriptionData();
        }
      }
    } catch {
      if (!cachedSubscribedRef.current && userId) {
        const found = await checkSubscriptionFromDB(userId, currentWorkspaceId, 1, currentRole);
        if (!found && !hasTransientError) resetSubscriptionData();
      }
    } finally {
      setLoading(false);
      hasCheckedRef.current = true;
      setInitialCheckDone(true);
      isCheckingRef.current = false;

      // If workspace resolved while we were checking, re-check with the new workspace
      if (pendingRecheckRef.current) {
        pendingRecheckRef.current = false;
        console.log("[Subscription] Executing pending re-check with resolved workspaceId");
        // Small delay to let React state settle
        setTimeout(() => checkSubscription(), 100);
      }
    }
  };


  useEffect(() => {
    const userId = user?.id ?? null;

    // When workspace just resolved and subscription wasn't found yet → force re-check
    if (userId === lastUserIdRef.current && hasCheckedRef.current) {
      const workspaceChanged = Boolean(
        storeWorkspaceId && lastCheckedWorkspaceIdRef.current !== storeWorkspaceId,
      );
      const needsRetryAfterTransient = !subscribed && !isAdmin && hasTransientErrorRef.current;
      if (!subscribed && !isAdmin && storeWorkspaceId && (workspaceChanged || needsRetryAfterTransient)) {
        console.log("[Subscription] Re-checking with resolved workspaceId for member access");
        hasCheckedRef.current = false;
        checkSubscription();
        setupRealtimeListeners(userId!, storeWorkspaceId);
      }
      return;
    }
    // If check is still running but workspace just resolved, queue re-check
    if (userId === lastUserIdRef.current && isCheckingRef.current && storeWorkspaceId && lastCheckedWorkspaceIdRef.current !== storeWorkspaceId) {
      pendingRecheckRef.current = true;
      return;
    }
    if (userId !== lastUserIdRef.current) {
      hasCheckedRef.current = false;
      isCheckingRef.current = false;
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      setHasTransientError(false);
      hasTransientErrorRef.current = false;
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      if (wsRealtimeChannelRef.current) {
        supabase.removeChannel(wsRealtimeChannelRef.current);
        wsRealtimeChannelRef.current = null;
      }
    }
    lastUserIdRef.current = userId;
    checkSubscription();

    // Setup realtime listeners (único ponto: 1 canal por user, 1 por workspace quando disponível)
    if (userId) {
      setupRealtimeListeners(userId, storeWorkspaceId);
    }

    // Polling reduced to 5 minutes (backup for realtime)
    const interval = setInterval(checkSubscription, 5 * 60 * 1000);
    return () => {
      clearInterval(interval);
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = null;
      }
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
      if (wsRealtimeChannelRef.current) {
        supabase.removeChannel(wsRealtimeChannelRef.current);
        wsRealtimeChannelRef.current = null;
      }
    };
  }, [user?.id, storeWorkspaceId]);

  // Quando o circuit breaker fechar (sai do modo degradado), refaz a checagem.
  useEffect(() => {
    const unsub = onCircuitChange((newState) => {
      if (newState === "CLOSED" && user?.id) {
        console.log("[Subscription] Circuit closed, re-checking subscription");
        hasCheckedRef.current = false;
        checkSubscription();
      }
    });
    return unsub;
  }, [user?.id]);

  const shouldShowPaywall =
    !subscribed &&
    !hasValidGracePeriod({ status, gracePeriodEnd }) &&
    !loading &&
    initialCheckDone &&
    !isAdmin;

  return (
    <SubscriptionContext.Provider
      value={{
        subscribed, productId, subscriptionEnd, loading, checkSubscription,
        isAdmin, shouldShowPaywall, status, inGracePeriod, gracePeriodEnd,
        failureCount, lastPaymentError, isWorkspaceMember,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
